import { createContext, useContext, useState, type FunctionComponent, type ReactNode } from "react";

import { type TeamMemberTaskWithTrainee } from "@fitness-app/data-models/entities/TeamMemberTask";

import { TeamMemberTaskFormModal } from "../components/TeamMemberTaskFormModal";
import { type TeamMemberTaskFormModel } from "../types";

interface TeamMemberTaskContextType {
  openTaskForm: (initialData?: Partial<TeamMemberTaskFormModel>) => void;
  closeTaskForm: () => void;
  isFormOpen: boolean;
  initialFormData: Partial<TeamMemberTaskFormModel> | null;
  editingTask: TeamMemberTaskWithTrainee | null;
  editTask: (task: TeamMemberTaskWithTrainee) => void;
  traineeId?: string;
}

const TeamMemberTaskContext = createContext<TeamMemberTaskContextType | undefined>(undefined);

interface TeamMemberTaskProviderProps {
  children: ReactNode;
  traineeId?: string;
}

export const TeamMemberTaskProvider: FunctionComponent<TeamMemberTaskProviderProps> = ({ children, traineeId }) => {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [initialFormData, setInitialFormData] = useState<Partial<TeamMemberTaskFormModel> | null>(null);
  const [editingTask, setEditingTask] = useState<TeamMemberTaskWithTrainee | null>(null);

  const openTaskForm = (initialData?: Partial<TeamMemberTaskFormModel>) => {
    setInitialFormData({ ...initialData, traineeId: traineeId || null });
    setEditingTask(null);
    setIsFormOpen(true);
  };

  const closeTaskForm = () => {
    setIsFormOpen(false);
    setInitialFormData(null);
    setEditingTask(null);
  };

  const editTask = (task: TeamMemberTaskWithTrainee) => {
    setEditingTask(task);
    setInitialFormData({
      title: task.title,
      description: task.description,
      dueDate: task.dueDate || { date: null, time: null, timezone: "Europe/Warsaw" },
      startDate: task.startDate || { date: null, time: null, timezone: "Europe/Warsaw" },
      endDate: task.endDate || { date: null, time: null, timezone: "Europe/Warsaw" },
      priority: task.priority,
      category: task.category,
      traineeId: task.traineeId,
      trainee: task.trainee,
      reminders: task.reminders || [],
      recurrence: task.recurrence,
      metadata: task.metadata,
      tags: task.tags || [],
    });
    setIsFormOpen(true);
  };

  const value: TeamMemberTaskContextType = {
    openTaskForm,
    closeTaskForm,
    isFormOpen,
    initialFormData,
    editingTask,
    editTask,
    traineeId,
  };

  return (
    <TeamMemberTaskContext.Provider value={value}>
      {children}
      <TeamMemberTaskFormModal />
    </TeamMemberTaskContext.Provider>
  );
};

export const useTeamMemberTaskContext = (): TeamMemberTaskContextType => {
  const context = useContext(TeamMemberTaskContext);
  if (context === undefined) {
    throw new Error("useTeamMemberTaskContext must be used within a TeamMemberTaskProvider");
  }
  return context;
};
